'use client';

import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';

import ko from './locales/ko.json';
import en from './locales/en.json';
import ja from './locales/ja.json';
import zh from './locales/zh.json';
import vi from './locales/vi.json';

export type Locale = 'ko' | 'en' | 'ja' | 'zh' | 'vi';

const LOCALES: Record<Locale, Record<string, any>> = { ko, en, ja, zh, vi };

export const LOCALE_LABELS: Record<Locale, { flag: string; name: string }> = {
    ko: { flag: '🇰🇷', name: '한국어' },
    en: { flag: '🇺🇸', name: 'English' },
    ja: { flag: '🇯🇵', name: '日本語' },
    zh: { flag: '🇨🇳', name: '中文' },
    vi: { flag: '🇻🇳', name: 'Tiếng Việt' },
};

type I18nContextType = {
    locale: Locale;
    setLocale: (l: Locale) => void;
    t: (key: string) => string;
};

const I18nContext = createContext<I18nContextType>({
    locale: 'ko',
    setLocale: () => { },
    t: (key: string) => key,
});

function getNestedValue(obj: any, path: string): string {
    return path.split('.').reduce((acc, part) => acc?.[part], obj) ?? path;
}

export function I18nProvider({ children }: { children: ReactNode }) {
    const [locale, setLocaleState] = useState<Locale>('ko');

    useEffect(() => {
        // Check browser language on mount
        const saved = localStorage.getItem('desinsight-locale') as Locale | null;
        if (saved && LOCALES[saved]) {
            // eslint-disable-next-line
            setLocaleState(saved);
        } else {
            const browserLang = navigator.language.slice(0, 2);
            const mapped: Record<string, Locale> = { ko: 'ko', en: 'en', ja: 'ja', zh: 'zh', vi: 'vi' };
            if (mapped[browserLang]) {
                // eslint-disable-next-line
                setLocaleState(mapped[browserLang]);
            }
        }
    }, []);

    const setLocale = useCallback((l: Locale) => {
        setLocaleState(l);
        localStorage.setItem('desinsight-locale', l);
        document.documentElement.lang = l;
    }, []);

    const t = useCallback(
        (key: string): string => {
            const val = getNestedValue(LOCALES[locale], key);
            // Fallback to Korean if not found
            if (val === key) return getNestedValue(LOCALES.ko, key);
            return val;
        },
        [locale]
    );

    return (
        <I18nContext.Provider value={{ locale, setLocale, t }}>
            {children}
        </I18nContext.Provider>
    );
}

export function useI18n() {
    return useContext(I18nContext);
}
